if (!customElements.get('video-list')) {
  class VideoList extends VisibleElement {
    constructor() {
      super();
      this.addEventListener("custom:visible", this.init.bind(this), { once: true });
    }

    init () {
      this.currentIndex = 0;

      try {
        const dataEl = this.querySelector('.video-list-data');
        this.videoData = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (e) {
        console.error('Failed to parse video list data', e);
        this.videoData = [];
      }

      this.track = this.querySelector('.video-list-track');
      this.progressBar = this.querySelector('.video-list-progress');
      this.items = Array.from(this.querySelectorAll('.video-list-item'));

      this.modal = this.querySelector('.video-list-modal');
      this.modalContent = this.querySelector('.modal-content');
      this.modalVideo = this.querySelector('.modal-video');
      this.modalCover = this.querySelector('.modal-cover');
      this.closeBtn = this.querySelector('.video-list-close');
      this.prevBtn = this.querySelector('.video-list-prev');
      this.nextBtn = this.querySelector('.video-list-next');

      this.trackPrevBtn = this.querySelector('.video-track-prev');
      this.trackNextBtn = this.querySelector('.video-track-next');

      this.addedToCartNotification = this.querySelector('.js-video-list-added-to-cart');

      this.bindEvents();
      this.updateProgressBar();
    }

    bindEvents () {
      if (this.track) {
        this.track.addEventListener('scroll', () => this.updateProgressBar());
      }

      this.items.forEach((item, index) => {
        item.addEventListener('click', () => {
          this.openModal(index);
        });
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.closeModal());
      }

      if (this.modal) {
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.closeModal();
        });
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.navigate(-1);
        });
      }

      const addToCartBtns = this.querySelectorAll('.modal-product-add');
      addToCartBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.addToCart(btn);
        });
      });

      const productClickArea = this.querySelector('.modal-product-click-area');
      if (productClickArea) {
        productClickArea.addEventListener('click', () => {
          if (this.currentProductUrl) {
            window.open(this.currentProductUrl, '_blank');
          }
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.navigate(1);
        });
      }

      const getScrollAmount = () => {
        // Scroll by the width of one visible item + gap, or a safe amount
        return this.track.clientWidth * 0.8;
      };

      if (this.trackPrevBtn) {
        this.trackPrevBtn.addEventListener('click', () => {
          if (this.track) {
            this.track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
          }
        });
      }

      if (this.trackNextBtn) {
        this.trackNextBtn.addEventListener('click', () => {
          if (this.track) {
            this.track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
          }
        });
      }
    }

    updateProgressBar () {
      if (!this.track || !this.progressBar) return;
      const scrollLeft = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      // base width for the thumb
      const thumbWidthRatio = this.track.clientWidth / this.track.scrollWidth;
      const baseWidthPercentage = thumbWidthRatio * 100;

      let progress = 0;
      if (maxScroll > 0) {
        progress = (scrollLeft / maxScroll) * (100 - baseWidthPercentage);
      }

      this.progressBar.style.width = `${Math.max(baseWidthPercentage, 10)}%`;
      this.progressBar.style.transform = `translateX(${(progress / baseWidthPercentage) * 100}%)`;

      if (this.trackPrevBtn) {
        this.trackPrevBtn.disabled = scrollLeft <= 1; // 1px threshold for safe comparison
      }
      if (this.trackNextBtn) {
        this.trackNextBtn.disabled = scrollLeft >= maxScroll - 1;
      }
    }

    openModal (index) {
      if (index < 0 || index >= this.videoData.length) return;
      this.currentIndex = index;

      this.populateModalData(this.videoData[index]);

      this.modal.classList.remove('tw-hidden');
      this.modal.classList.add('tw-flex');
      // trigger reflow
      void this.modal.offsetWidth;
      this.modal.classList.remove('tw-opacity-0');

      if (this.modalContent) {
        this.modalContent.classList.remove('tw-scale-95', 'tw-opacity-0');
        this.modalContent.classList.add('tw-scale-100', 'tw-opacity-100');
      }

      if (this.prevBtn) {
        this.prevBtn.classList.remove('tw-opacity-0', 'tw-translate-x-4');
      }

      if (this.nextBtn) {
        this.nextBtn.classList.remove('tw-opacity-0', 'tw-translate-x-4');
      }

      this.updateNavButtons();

      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    closeModal () {
      this.modal.classList.add('tw-opacity-0');

      if (this.modalContent) {
        this.modalContent.classList.remove('tw-scale-100', 'tw-opacity-100');
        this.modalContent.classList.add('tw-scale-95', 'tw-opacity-0');
      }

      if (this.prevBtn) {
        this.prevBtn.classList.add('tw-opacity-0', 'tw-translate-x-4');
      }

      if (this.nextBtn) {
        this.nextBtn.classList.add('tw-opacity-0', 'tw-translate-x-4');
      }

      setTimeout(() => {
        this.modal.classList.add('tw-hidden');
        this.modal.classList.remove('tw-flex');
        if (this.modalVideo) {
          this.modalVideo.pause();
          this.modalVideo.src = '';
        }
        document.body.style.overflow = '';
      }, 300);
    }

    navigate (direction) {
      let newIndex = this.currentIndex + direction;
      if (newIndex >= 0 && newIndex < this.videoData.length) {
        this.currentIndex = newIndex;
        this.populateModalData(this.videoData[this.currentIndex]);
        this.updateNavButtons();
      }
    }

    updateNavButtons () {
      if (this.prevBtn) {
        this.prevBtn.style.visibility = this.currentIndex > 0 ? 'visible' : 'hidden';
      }
      if (this.nextBtn) {
        this.nextBtn.style.visibility = this.currentIndex < this.videoData.length - 1 ? 'visible' : 'hidden';
      }
    }

    populateModalData (data) {
      if (!data) return;

      this.currentVariantId = data.variant_id || null;

      // Populate Video (or Cover image fallback when video_url is empty)
      const hasVideo = !!(data && data.video_url);

      if (this.modalVideo) {
        if (hasVideo) {
          this.modalVideo.classList.remove('tw-hidden');
          this.modalVideo.src = data.video_url;
          this.modalVideo.load();
          this.modalVideo.play().catch(e => console.log("Autoplay prevented:", e));
        } else {
          // Pause and detach so it stops loading / playing.
          try { this.modalVideo.pause(); } catch (_) { }
          this.modalVideo.removeAttribute('src');
          try { this.modalVideo.load(); } catch (_) { }
          this.modalVideo.classList.add('tw-hidden');
        }
      }

      if (this.modalCover) {
        if (!hasVideo && data && data.cover) {
          this.modalCover.src = data.cover;
          this.modalCover.alt = data.author ? `Cover by ${data.author}` : '';
          this.modalCover.classList.remove('tw-hidden');
        } else {
          this.modalCover.removeAttribute('src');
          this.modalCover.alt = '';
          this.modalCover.classList.add('tw-hidden');
        }
      }

      // Populate Author details
      const authorEl = this.querySelector('.modal-author-name');
      const avatarEl = this.querySelector('.modal-author-avatar');

      if (authorEl) authorEl.textContent = data.author || '';
      if (avatarEl) {
        if (data.author_avatar) {
          avatarEl.src = data.author_avatar;
          avatarEl.classList.remove('hidden');
        } else {
          avatarEl.classList.add('hidden');
        }
      }

      // Description
      const descEl = this.querySelector('.modal-description');
      if (descEl) {
        descEl.innerHTML = data.description ? data.description.replace(/\\n/g, '<br>') : '';
      }

      // Product
      const productWrap = this.querySelector('.modal-product-wrap');
      const productTitle = this.querySelector('.modal-product-title');
      const productPrice = this.querySelector('.modal-product-price');
      const productImg = this.querySelector('.modal-product-img');
      const mobileAddWrap = this.querySelector('.modal-mobile-add-to-cart-wrap');

      this.currentProductUrl = data.product_url || null;

      if (data.product_title || data.product_price) {
        if (productWrap) productWrap.classList.remove('hidden');
        if (mobileAddWrap) mobileAddWrap.classList.remove('hidden');
        if (productTitle) productTitle.textContent = data.product_title || '';
        if (productPrice) productPrice.innerHTML = data.product_price || ''; // using innerHTML in case money() outputs HTML

        if (productImg) {
          if (data.product_image) {
            productImg.src = data.product_image;
            productImg.parentElement.classList.remove('hidden');
          } else {
            productImg.parentElement.classList.add('hidden');
          }
        }
      } else {
        if (productWrap) productWrap.classList.add('hidden');
        if (mobileAddWrap) mobileAddWrap.classList.add('hidden');
      }

      // Collection
      const collectionWrap = this.querySelector('.modal-collection-wrap');
      const collectionLink = this.querySelector('.modal-collection-link');
      if (data.collection_url) {
        if (collectionWrap) collectionWrap.classList.remove('hidden');
        if (collectionLink) collectionLink.href = data.collection_url;
      } else {
        if (collectionWrap) collectionWrap.classList.add('hidden');
      }

      // Reset the success toast when navigating to a new product
      if (this.addedToCartNotification) this.addedToCartNotification.hidden = true;
    }

    async addToCart (btn) {
      if (!this.currentVariantId) return;

      const originalText = btn.textContent;
      btn.textContent = 'Adding...';
      btn.disabled = true;

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        const oldCartData = oldCartResponse.ok ? await oldCartResponse.json() : null;

        // Build FormData like ProductForm to get rendered sections back
        const formData = new FormData();
        formData.append('id', this.currentVariantId);
        formData.append('quantity', 1);
        let sections = 'cart-icon-bubble';

        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);

        const fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        };

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();

        // handle possible errors returned by theme endpoint
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((it) => it[1].join(', '));
        }

        if (data.status) {
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error }
          }));
          return;
        }

        if (!response.ok) {
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: response.statusText }
          }));
          return;
        }

        // Update cart icon and drawer similarly to ProductForm
        if (typeof ProductForm !== 'undefined' && typeof ProductForm.updateCartIcon === 'function') {
          ProductForm.updateCartIcon(data);
        } else {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble && data?.sections && data.sections['cart-icon-bubble']) {
            cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
          }
        }





        // Show "Added to cart" notification (mirrors quick-add-drawer addedToCart pattern)
        if (this.addedToCartNotification) {
          const visibleFor = Number(this.addedToCartNotification.dataset.visibleFor) || 2000;
          setTimeout(() => {
            this.addedToCartNotification.hidden = false;
          }, 300);
          setTimeout(() => {
            this.addedToCartNotification.hidden = true;
          }, 300 + visibleFor);
        }

        // Optional haptic feedback on ATC success, mirroring product-form.js
        if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
          window.navigator.vibrate(100);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        const newCartData = newCartResponse.ok ? await newCartResponse.json() : null;

        const itemInOldCart = oldCartData?.items?.find((oldCartItem) => oldCartItem.variant_id === data.variant_id);
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (e) {
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: e.message || 'Add to cart failed'
          }
        }));
      } finally {
        btn.textContent = originalText;
        btn.disabled = false;

      }
    }
  }

  customElements.define('video-list', VideoList);
}
